import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import { getTenant, updateTenant, Tenant, uploadFile } from '../services/api';
import Cropper from 'react-easy-crop';

interface LoginPageConfigProps {
  skipLayout?: boolean;
}

const LoginPageConfig: React.FC<LoginPageConfigProps> = ({ skipLayout = false }) => {
  const { user, canModifyTenantSettings } = useAuth();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [uploading, setUploading] = useState<{
    screenBackground: boolean;
    pageLogo: boolean;
    formLogo: boolean;
    ownerImage: boolean;
  }>({
    screenBackground: false,
    pageLogo: false,
    formLogo: false,
    ownerImage: false,
  });
  const [uploadErrors, setUploadErrors] = useState<{
    screenBackground: string;
    pageLogo: string;
    formLogo: string;
    ownerImage: string;
  }>({
    screenBackground: '',
    pageLogo: '',
    formLogo: '',
    ownerImage: '',
  });

  // Cropper state
  const [cropperOpen, setCropperOpen] = useState(false);
  const [croppingField, setCroppingField] = useState<'ownerImage' | 'screenBackground' | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [imageFile, setImageFile] = useState<File | null>(null);

  const [formData, setFormData] = useState({
    formDisplaySide: 'left' as 'left' | 'right',
    screenBackground: '',
    pageLogo: '',
    formLogo: '',
    ownerImage: '',
    ownerName: '',
    ownerDesignation: '',
    title: '',
    subtitle: '',
  });

  const [originalFormData, setOriginalFormData] = useState({
    formDisplaySide: 'left' as 'left' | 'right',
    screenBackground: '',
    pageLogo: '',
    formLogo: '',
    ownerImage: '',
    ownerName: '',
    ownerDesignation: '',
    title: '',
    subtitle: '',
  });

  useEffect(() => {
    const fetchTenant = async () => {
      if (!user?.tenantId) {
        setError('No tenant associated with your account');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await getTenant(user.tenantId);
        if (response.data.success && response.data.data) {
          const tenantData = response.data.data;
          setTenant(tenantData);
          const loginConfig = tenantData.spec.login_config || {};
          
          const initialData = {
            formDisplaySide: (loginConfig.formDisplaySide || 'left') as 'left' | 'right',
            screenBackground: loginConfig.screenBackground || '',
            pageLogo: loginConfig.pageLogo || '',
            formLogo: loginConfig.formLogo || '',
            ownerImage: loginConfig.ownerImage || '',
            ownerName: loginConfig.ownerName || '',
            ownerDesignation: loginConfig.ownerDesignation || '',
            title: loginConfig.title || '',
            subtitle: loginConfig.subtitle || '',
          };
          setFormData(initialData);
          setOriginalFormData(initialData);
        }
      } catch (err: any) {
        setError('Failed to load tenant details');
        console.error('Failed to load tenant:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTenant();
  }, [user?.tenantId]);

  const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', (error) => reject(error));
      image.setAttribute('crossOrigin', 'anonymous');
      image.src = url;
    });

  const getCroppedImg = async (imageSrc: string, pixelCrop: any, aspectRatio?: number): Promise<Blob> => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Canvas not supported');
    }

    if (aspectRatio) {
      // For square crop (ownerImage)
      const size = Math.min(pixelCrop.width, pixelCrop.height);
      const sx = pixelCrop.x + (pixelCrop.width - size) / 2;
      const sy = pixelCrop.y + (pixelCrop.height - size) / 2;
      canvas.width = size;
      canvas.height = size;
      ctx.drawImage(image, sx, sy, size, size, 0, 0, size, size);
    } else {
      // For cover crop (screenBackground) - use the full cropped area
      canvas.width = pixelCrop.width;
      canvas.height = pixelCrop.height;
      ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height
      );
    }

    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to create image blob'));
        }
      }, 'image/jpeg', 0.9);
    });
  };

  const onCropComplete = useCallback((_: any, croppedArea: any) => {
    setCroppedAreaPixels(croppedArea);
  }, []);

  const handleFileUpload = async (
    file: File,
    field: 'screenBackground' | 'pageLogo' | 'formLogo' | 'ownerImage'
  ) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setUploadErrors((prev) => ({
        ...prev,
        [field]: 'Please select an image file',
      }));
      return;
    }
    const maxBytes = 3 * 1024 * 1024;
    if (file.size > maxBytes) {
      const { formatFileSizeMB } = await import('../utils/imageCompression');
      setUploadErrors((prev) => ({
        ...prev,
        [field]: `The selected image (${formatFileSizeMB(file.size)}) exceeds the maximum allowed size (3 MB). Please choose a smaller file.`,
      }));
      return;
    }

    setUploadErrors((prev) => ({ ...prev, [field]: '' }));

    // For ownerImage and screenBackground, open cropper
    if (field === 'ownerImage' || field === 'screenBackground') {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        setImagePreview(result);
        setImageFile(file);
        setCroppingField(field);
        setCropperOpen(true);
        setZoom(1);
        setCrop({ x: 0, y: 0 });
      };
      reader.onerror = () => setUploadErrors((prev) => ({ ...prev, [field]: 'Failed to read file' }));
      reader.readAsDataURL(file);
      return;
    }

    // For other fields, upload directly
    setUploading((prev) => ({ ...prev, [field]: true }));
    const run = async () => {
      try {
        const { compressImage } = await import('../utils/imageCompression');
        const maxSize = 800;
        const maxKB = 300;
        const compressedFile = await compressImage(file, maxSize, maxSize, 0.8, maxKB);

        const formDataUpload = new FormData();
        formDataUpload.append('file', compressedFile);
        formDataUpload.append('folderPath', `images/login-config/${field}`);
        const response = await uploadFile(formDataUpload);
        const url = response.data?.data?.url;
        if (url) {
          setFormData((prev) => ({
            ...prev,
            [field]: url,
          }));
        } else {
          setUploadErrors((prev) => ({
            ...prev,
            [field]: 'Upload succeeded but no URL returned.',
          }));
        }
      } catch (err: any) {
        setUploadErrors((prev) => ({
          ...prev,
          [field]: err.response?.data?.message || err.message || 'Failed to upload image',
        }));
      } finally {
        setUploading((prev) => ({ ...prev, [field]: false }));
      }
    };
    setTimeout(run, 0);
  };

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: 'screenBackground' | 'pageLogo' | 'formLogo' | 'ownerImage'
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    handleFileUpload(file, field);
    if (e.target) e.target.value = '';
  };

  const handleCloseCropper = () => {
    setCropperOpen(false);
    setCroppingField(null);
    setImageFile(null);
    setImagePreview('');
    setUploadErrors((prev) => ({
      ...prev,
      ownerImage: '',
      screenBackground: '',
    }));
  };

  const handleUploadCropped = async () => {
    if (!imagePreview || !croppedAreaPixels || !croppingField) {
      if (croppingField) {
        const field = croppingField;
        setUploadErrors((prev) => ({
          ...prev,
          [field]: 'Please select an area to crop',
        }));
      }
      return;
    }

    // TypeScript now knows croppingField is not null
    const field = croppingField;

    setUploading((prev) => ({ ...prev, [field]: true }));
    setUploadErrors((prev) => ({ ...prev, [field]: '' }));
    const run = async () => {
      try {
        const aspectRatio = field === 'ownerImage' ? 1 : undefined;
        const croppedBlob = await getCroppedImg(imagePreview, croppedAreaPixels, aspectRatio);
        const croppedFile = new File(
          [croppedBlob],
          imageFile?.name || `${field}.jpg`,
          { type: 'image/jpeg' }
        );

        const { compressImage } = await import('../utils/imageCompression');
        const maxSize = field === 'screenBackground' ? 1920 : 800;
        const maxKB = field === 'screenBackground' ? 500 : 300;
        const compressedFile = await compressImage(croppedFile, maxSize, maxSize, 0.8, maxKB);

        const formDataUpload = new FormData();
        formDataUpload.append('file', compressedFile);
        formDataUpload.append('folderPath', `images/login-config/${field}`);
        const response = await uploadFile(formDataUpload);
        const url = response.data?.data?.url;
        if (url) {
          setFormData((prev) => ({
            ...prev,
            [field]: url,
          }));
          handleCloseCropper();
        } else {
          setUploadErrors((prev) => ({
            ...prev,
            [field]: 'Upload succeeded but no URL returned.',
          }));
        }
      } catch (err: any) {
        setUploadErrors((prev) => ({
          ...prev,
          [field]: err.response?.data?.message || err.message || 'Failed to upload cropped image',
        }));
      } finally {
        setUploading((prev) => ({ ...prev, [field]: false }));
      }
    };
    setTimeout(run, 0);
  };

  const handleClearImage = (field: 'screenBackground' | 'pageLogo' | 'formLogo' | 'ownerImage') => {
    setFormData((prev) => ({
      ...prev,
      [field]: '',
    }));
    setUploadErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleEdit = () => {
    if (!canModifyTenantSettings) return;
    setIsEditMode(true);
  };

  const handleCancel = () => {
    setIsEditMode(false);
    // Reset form data to original
    setFormData({ ...originalFormData });
    setUploadErrors({
      screenBackground: '',
      pageLogo: '',
      formLogo: '',
      ownerImage: '',
    });
  };

  // Check if there are unsaved changes
  const hasChanges = () => {
    return (
      formData.formDisplaySide !== originalFormData.formDisplaySide ||
      formData.screenBackground !== originalFormData.screenBackground ||
      formData.pageLogo !== originalFormData.pageLogo ||
      formData.formLogo !== originalFormData.formLogo ||
      formData.ownerImage !== originalFormData.ownerImage ||
      formData.ownerName.trim() !== originalFormData.ownerName.trim() ||
      formData.ownerDesignation.trim() !== originalFormData.ownerDesignation.trim() ||
      formData.title.trim() !== originalFormData.title.trim() ||
      formData.subtitle.trim() !== originalFormData.subtitle.trim()
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canModifyTenantSettings) return;
    if (saving) return;
    setError('');
    setSuccess(false);

    if (!tenant) {
      setError('Tenant not loaded');
      return;
    }

    setSaving(true);
    try {

      const data: any = {
        spec: {
          login_config: {
            formDisplaySide: formData.formDisplaySide,
            screenBackground: formData.screenBackground || undefined,
            pageLogo: formData.pageLogo || undefined,
            formLogo: formData.formLogo || undefined,
            ownerImage: formData.ownerImage || undefined,
            ownerName: formData.ownerName.trim() || undefined,
            ownerDesignation: formData.ownerDesignation.trim() || undefined,
            title: formData.title.trim() || undefined,
            subtitle: formData.subtitle.trim() || undefined,
          },
        },
      };

      const response = await updateTenant(tenant._id, data);

      if (response.data.success) {
        setSuccess(true);
        setTenant(response.data.data);
        // Update original form data to current state after successful save
        setOriginalFormData({ ...formData });
        setIsEditMode(false); // Exit edit mode after successful save
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setTimeout(() => setSuccess(false), 5000);
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to update login page config';
      setError(errorMessage);
      console.error('Failed to update login page config:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    const loadingContent = <div className="loading">Loading login page config...</div>;
    if (skipLayout) return loadingContent;
    return <DashboardLayout>{loadingContent}</DashboardLayout>;
  }

  if (!tenant) {
    const errorContent = <div className="error-message">{error || 'Tenant not found'}</div>;
    if (skipLayout) return errorContent;
    return <DashboardLayout>{errorContent}</DashboardLayout>;
  }

  const renderImageUpload = (
    field: 'screenBackground' | 'pageLogo' | 'formLogo' | 'ownerImage',
    label: string,
    description?: string
  ) => {
    const value = formData[field];
    const isUploading = uploading[field];
    const uploadError = uploadErrors[field];

    return (
      <div className="form-group">
        <label>{label}</label>
        <div className="avatar-upload-row">
          <input
            type="url"
            value={value}
            onChange={(e) =>
              setFormData({
                ...formData,
                [field]: e.target.value,
              })
            }
            placeholder="https://example.com/image.png"
            style={{ flex: '1 1 260px' }}
            disabled={!isEditMode}
          />
          {!value && isEditMode && (
            <label className={`avatar-upload-button ${isUploading ? 'disabled' : ''}`} style={{ marginBottom: 0 }}>
              <input
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => handleFileChange(e, field)}
                disabled={isUploading || !isEditMode}
              />
              {isUploading ? (
                <>
                  <span className="loading-spinner btn-spinner" aria-hidden style={{ marginRight: '8px' }} />
                  Uploading...
                </>
              ) : (
                <>
                  <span role="img" aria-label="upload" style={{ marginRight: '8px', display: 'inline-block' }}>
                    📤
                  </span>
                  Upload
                </>
              )}
            </label>
          )}
          {isUploading && (
            <small style={{ color: '#2563eb', fontSize: '14px', marginTop: '4px', display: 'block' }}>
              Processing & uploading...
            </small>
          )}
          {value && isEditMode && (
            <button
              type="button"
              onClick={() => handleClearImage(field)}
              className="btn-secondary avatar-clear-button"
            >
              Clear
            </button>
          )}
        </div>
        {uploadError && (
          <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
            {uploadError}
          </small>
        )}
        {description && (
          <small style={{ color: '#666', fontSize: '14px', marginTop: '4px', display: 'block' }}>
            {description}
          </small>
        )}
        {!value && isEditMode && (
          <small style={{ color: '#374151', fontWeight: 600, fontSize: '14px', marginTop: '2px', display: 'block' }}>
            Max size: 3MB.
          </small>
        )}
        {value && (
          <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: field === 'screenBackground' ? 200 : 120,
                height: field === 'screenBackground' ? 120 : 120,
                borderRadius: 8,
                overflow: 'hidden',
                border: '1px solid #e5e7eb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: (field === 'pageLogo' || field === 'formLogo') 
                  ? 'repeating-conic-gradient(#f0f0f0 0% 25%, #ffffff 0% 50%) 50% / 20px 20px'
                  : 'transparent',
              }}
            >
              <img
                src={value}
                alt={`${label} preview`}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: field === 'screenBackground' ? 'cover' : 'contain',
                  display: 'block',
                  backgroundColor: 'transparent',
                }}
              />
            </div>
            <span className="avatar-preview-text" style={{ maxWidth: '300px', wordBreak: 'break-all' }}>
              {value}
            </span>
          </div>
        )}
      </div>
    );
  };

  const content = (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 270px)' }}>
        <div style={{ flexShrink: 0 }}>
          {error && <div className="error-message">{error}</div>}
          {success && (
            <div
              className="success-message"
              style={{
                backgroundColor: '#d4edda',
                color: '#155724',
                padding: '12px',
                borderRadius: '6px',
                marginBottom: '20px',
              }}
            >
              Login page config updated successfully!
            </div>
          )}
        </div>

        <div style={{ flex: '1', overflowY: 'auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <div className="card" style={{ flex: '1', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: '1', minHeight: 0 }}>
              <div style={{ flex: '1', overflowY: 'auto', minHeight: 0 }}>
                <div className="form-group">
                  <label>Form Display Side</label>
                  <select
                    value={formData.formDisplaySide}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        formDisplaySide: e.target.value as 'left' | 'right',
                      })
                    }
                    style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                    disabled={!isEditMode}
                  >
                    <option value="left">Left</option>
                    <option value="right">Right</option>
                  </select>
                  <small style={{ color: '#666', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                    Choose which side the login form appears on
                  </small>
                </div>

                {renderImageUpload(
                  'screenBackground',
                  'Screen Background',
                  'Upload an image or paste a URL for the login page background. Recommended: 1920x1080 or larger.'
                )}

                {renderImageUpload(
                  'pageLogo',
                  'Page Logo',
                  'Upload an image or paste a URL for the main logo displayed on the login page.'
                )}

                {renderImageUpload(
                  'formLogo',
                  'Form Logo',
                  'Upload an image or paste a URL for the logo displayed on the login form.'
                )}

                {renderImageUpload(
                  'ownerImage',
                  'Owner Image',
                  'Upload an image or paste a URL for the owner/founder image displayed on the login page.'
                )}

                <div className="form-group">
                  <label>Owner Name</label>
                  <input
                    type="text"
                    value={formData.ownerName}
                    onChange={(e) => setFormData({ ...formData, ownerName: e.target.value })}
                    placeholder="e.g., Dr. John Smith"
                    disabled={!isEditMode}
                  />
                </div>

                <div className="form-group">
                  <label>Owner Designation</label>
                  <input
                    type="text"
                    value={formData.ownerDesignation}
                    onChange={(e) => setFormData({ ...formData, ownerDesignation: e.target.value })}
                    placeholder="e.g., Founder & CEO"
                    disabled={!isEditMode}
                  />
                </div>

                <div className="form-group">
                  <label>Title</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g., Ambulance Management System"
                    disabled={!isEditMode}
                  />
                </div>

                <div className="form-group">
                  <label>Subtitle</label>
                  <input
                    type="text"
                    value={formData.subtitle}
                    onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                    placeholder="e.g., Efficient emergency medical services management"
                    disabled={!isEditMode}
                  />
                </div>
              </div>

              <div
                style={{
                  flexShrink: 0,
                  marginBottom: '-15px',
                  marginTop: '15px',
                  paddingTop: '16px',
                  paddingBottom: '8px',
                  borderTop: '1px solid #e2e8f0',
                  display: 'flex',
                  gap: '12px',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                }}
              >
                {!isEditMode ? (
                  canModifyTenantSettings ? (
                  <button
                    type="button"
                    onClick={handleEdit}
                    className="btn-submit"
                    style={{
                      backgroundColor: '#2563eb',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '10px 20px',
                      fontSize: '16px',
                      fontWeight: '500',
                      cursor: 'pointer',
                    }}
                  >
                    Edit
                  </button>
                  ) : null
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={handleCancel}
                      className="btn-secondary"
                      style={{
                        padding: '10px 20px',
                        backgroundColor: '#e5e7eb',
                        color: '#374151',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '16px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        marginTop: '0px',
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn-submit"
                      disabled={saving || !hasChanges()}
                      style={{
                        opacity: hasChanges() ? 1 : 0.6,
                        cursor: hasChanges() ? 'pointer' : 'not-allowed',
                      }}
                    >
                      {saving ? (
                        <>
                          <span className="loading-spinner btn-spinner" aria-hidden />
                          Saving...
                        </>
                      ) : (
                        'Save Changes'
                      )}
                    </button>
                  </>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Cropper Modal */}
      {cropperOpen && croppingField && (
        <div className="cropper-modal-overlay" onClick={handleCloseCropper}>
          <div className="cropper-modal" onClick={(e) => e.stopPropagation()}>
            <h4 style={{ marginBottom: '12px' }}>
              Crop {croppingField === 'ownerImage' ? 'Owner Image' : 'Screen Background'}
            </h4>
            <div className="cropper-container">
              <Cropper
                image={imagePreview}
                crop={crop}
                zoom={zoom}
                aspect={croppingField === 'ownerImage' ? 1 : undefined} // Square for owner, free for background
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                cropShape={croppingField === 'ownerImage' ? 'rect' : 'rect'} // Square for owner
                showGrid={false}
              />
            </div>
            <div className="cropper-controls">
              <label style={{ fontSize: '14px', color: '#6b7280' }}>Zoom</label>
              <input
                type="range"
                min={1}
                max={3}
                step={0.1}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
              />
            </div>
            {uploadErrors[croppingField] && (
              <div className="error-message" style={{ marginTop: '8px' }}>
                {uploadErrors[croppingField]}
              </div>
            )}
            <div className="cropper-actions">
              <button
                type="button"
                className="btn-secondary"
                style={{ marginTop: 0 }}
                onClick={handleCloseCropper}
                disabled={uploading[croppingField]}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleUploadCropped}
                disabled={uploading[croppingField]}
              >
                {uploading[croppingField] ? 'Uploading...' : 'Save & Upload'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  if (skipLayout) {
    return content;
  }

  return <DashboardLayout>{content}</DashboardLayout>;
};

export default LoginPageConfig;
